import type { CommunityProgram } from '@/mocks/adminCommunity';

interface ProgramCardProps {
  program: CommunityProgram;
}

const categoryColors: Record<string, { bg: string; dot: string; text: string; border: string }> = {
  musica:         { bg: 'bg-emerald-50',  dot: 'bg-emerald-500',  text: 'text-emerald-600',  border: 'border-emerald-200' },
  educacion:      { bg: 'bg-amber-50',    dot: 'bg-amber-500',    text: 'text-amber-600',    border: 'border-amber-200' },
  deportes:       { bg: 'bg-red-50',      dot: 'bg-red-500',      text: 'text-red-600',      border: 'border-red-200' },
  cultura:        { bg: 'bg-violet-50',   dot: 'bg-violet-500',   text: 'text-violet-600',   border: 'border-violet-200' },
  entretenimiento:{ bg: 'bg-sky-50',      dot: 'bg-sky-500',      text: 'text-sky-600',      border: 'border-sky-200' },
};

const categoryLabels: Record<string, string> = {
  musica: 'Música',
  educacion: 'Educación',
  deportes: 'Deportes',
  cultura: 'Cultura',
  entretenimiento: 'Entretenimiento',
};

export default function ProgramCard({ program }: ProgramCardProps) {
  const cat = categoryColors[program.category] || categoryColors.entretenimiento;

  return (
    <div className={`flex-shrink-0 w-[280px] rounded-2xl border ${cat.border} ${cat.bg} p-4 flex flex-col gap-3 hover:scale-[1.02] transition-transform duration-200 cursor-default`}>
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className={`w-10 h-10 rounded-xl ${cat.bg} flex items-center justify-center shrink-0`}>
          <i className={`${program.icon} ${cat.text} text-lg`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-foreground-950 font-bold text-sm leading-tight">{program.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${cat.bg} ${cat.text} text-[9px] font-semibold`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`}></span>
              {categoryLabels[program.category]}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-foreground-500 text-[11px] leading-relaxed line-clamp-3">{program.description}</p>

      {/* Host + Schedule */}
      <div className="bg-background-50/80 rounded-xl p-2.5 space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center shrink-0">
            <i className="ri-user-line text-foreground-400 text-[10px]"></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-700 text-[10px] font-semibold truncate">{program.hostName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <i className="ri-time-line text-foreground-400 text-xs shrink-0"></i>
          <p className="text-foreground-500 text-[10px]">{program.schedule}</p>
        </div>
      </div>

      {/* Stats + active */}
      <div className="flex items-center justify-between pt-1 border-t border-background-200/60">
        <div className="flex items-center gap-1">
          <i className="ri-group-line text-foreground-400 text-xs"></i>
          <span className="text-foreground-700 text-xs font-semibold">{program.participants}</span>
          <span className="text-foreground-400 text-[10px]">participantes</span>
        </div>
        {program.isActive && (
          <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Activo
          </span>
        )}
      </div>
    </div>
  );
}